import { useEffect, useState } from "react";
import { FileText, Pencil, Plus, Power, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import CriticalPinDialog from "../CriticalPinDialog";

const slugify = (t) =>
    t.toLowerCase()
        .replace(/ç/g, "c").replace(/ğ/g, "g").replace(/ı/g, "i")
        .replace(/ö/g, "o").replace(/ş/g, "s").replace(/ü/g, "u")
        .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "sayfa";

export default function PagesTab() {
    const [pages, setPages] = useState(null);
    const [editor, setEditor] = useState(null); // {page_id?, title, slug, active, sections:[{h, text}], isNew, builtin}
    const [busy, setBusy] = useState(false);
    const [pinTarget, setPinTarget] = useState(null);

    const load = () => api.get("/admin/pages").then((r) => setPages(r.data)).catch(() => {});
    useEffect(() => { load(); }, []);

    const openEditor = (p) => {
        setEditor({
            page_id: p?.page_id || null,
            title: p?.title || "",
            slug: p?.slug || "",
            active: p?.active ?? true,
            sections: (p?.sections || []).map((s) => ({ h: s.h, text: (s.p || []).join("\n") })),
            isNew: !p,
            builtin: !!p?.builtin,
        });
    };

    const save = async () => {
        if (!editor.title.trim()) return toast.error("Başlık zorunludur");
        setBusy(true);
        const payload = {
            title: editor.title,
            slug: editor.slug || undefined,
            active: editor.active,
            sections: editor.sections.filter((s) => s.h.trim() || s.text.trim()).map((s) => ({
                h: s.h.trim(),
                p: s.text.split("\n").map((x) => x.trim()).filter(Boolean),
            })),
        };
        try {
            if (editor.isNew) {
                await api.post("/admin/pages", payload);
                toast.success("Sayfa oluşturuldu ve yayına alındı");
            } else {
                await api.put(`/admin/pages/${editor.page_id}`, payload);
                toast.success("Sayfa güncellendi");
            }
            setEditor(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kaydedilemedi");
        } finally {
            setBusy(false);
        }
    };

    const toggleActive = async (p) => {
        try {
            await api.put(`/admin/pages/${p.page_id}`, { active: !p.active });
            toast.success(`${p.title} ${!p.active ? "yayında" : "yayından kaldırıldı (pasif)"}`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "İşlem başarısız");
        }
    };

    const remove = async (p, pin) => {
        if (pin === undefined) {
            setPinTarget(p);
            return;
        }
        try {
            await api.delete(`/admin/pages/${p.page_id}`, { headers: { "X-Critical-Pin": pin } });
            toast.success("Sayfa kalıcı olarak silindi");
            setPinTarget(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Silinemedi");
        }
    };

    const setSection = (i, patch) => setEditor((e) => ({ ...e, sections: e.sections.map((s, j) => (j === i ? { ...s, ...patch } : s)) }));

    return (
        <div data-testid="pages-tab">
            <CriticalPinDialog open={!!pinTarget} onClose={() => setPinTarget(null)} onSubmit={(pin) => remove(pinTarget, pin)} actionLabel={pinTarget ? `Sayfa silme: ${pinTarget.title}` : ""} />
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-muted-foreground">Yasal ve içerik sayfalarını düzenleyin; yeni sayfa ekleyin. Pasif sayfalar sitede görünmez.</p>
                <Button onClick={() => openEditor(null)} className="bg-terra-600 hover:bg-terra-700 text-white" data-testid="page-new-button">
                    <Plus className="mr-2 h-4 w-4" /> Yeni Sayfa
                </Button>
            </div>

            {!pages ? (
                <div className="space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-16 animate-pulse rounded-2xl bg-muted" />)}</div>
            ) : (
                <div className="overflow-hidden rounded-2xl border bg-card shadow-sm">
                    {pages.map((p) => (
                        <div key={p.page_id} className={`flex flex-wrap items-center gap-3 border-b px-5 py-4 last:border-0 ${!p.active ? "opacity-55" : ""}`} data-testid={`page-row-${p.page_id}`}>
                            <FileText className="h-5 w-5 shrink-0 text-terra-600" />
                            <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-2">
                                    <p className="font-semibold text-sm">{p.title}</p>
                                    <Badge variant="outline" className={`text-[10px] ${p.builtin ? "border-blue-200 bg-blue-50 text-blue-700" : "border-purple-200 bg-purple-50 text-purple-700"}`}>
                                        {p.builtin ? "Yerleşik" : "Özel"}
                                    </Badge>
                                    <Badge variant="outline" className={`text-[10px] ${p.active ? "border-emerald-200 bg-emerald-100 text-emerald-800" : "border-red-200 bg-red-100 text-red-700"}`} data-testid={`page-active-badge-${p.page_id}`}>
                                        {p.active ? "Yayında" : "Pasif"}
                                    </Badge>
                                </div>
                                <p className="mt-0.5 text-xs text-muted-foreground">
                                    {p.builtin ? `/${p.slug}` : `/sayfa/${p.slug}`} • {p.sections.length} bölüm • Son güncelleme: {p.updated}
                                </p>
                            </div>
                            <div className="flex gap-2">
                                <Button size="sm" variant="outline" onClick={() => openEditor(p)} data-testid={`page-edit-${p.page_id}`}>
                                    <Pencil className="mr-1.5 h-3.5 w-3.5" /> Düzenle
                                </Button>
                                <Button size="sm" variant="outline" className={p.active ? "text-destructive" : "text-emerald-700"} onClick={() => toggleActive(p)} data-testid={`page-toggle-${p.page_id}`}>
                                    <Power className="mr-1.5 h-3.5 w-3.5" /> {p.active ? "Pasifleştir" : "Yayınla"}
                                </Button>
                                {!p.builtin && (
                                    <Button size="sm" variant="outline" className="text-destructive" onClick={() => remove(p)} data-testid={`page-delete-${p.page_id}`}>
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Dialog open={!!editor} onOpenChange={() => setEditor(null)}>
                <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="page-editor-dialog">
                    {editor && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="font-display">{editor.isNew ? "Yeni Sayfa Oluştur" : `Düzenle — ${editor.title}`}</DialogTitle>
                                <DialogDescription>Her bölüm için başlık ve içerik girin. İçerikte her satır ayrı paragraf olur.</DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4">
                                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                    <div>
                                        <p className="mb-1 text-xs font-medium text-muted-foreground">Sayfa Başlığı *</p>
                                        <Input value={editor.title} onChange={(e) => setEditor({ ...editor, title: e.target.value, ...(editor.isNew ? { slug: slugify(e.target.value) } : {}) })} data-testid="page-title-input" />
                                    </div>
                                    <div>
                                        <p className="mb-1 text-xs font-medium text-muted-foreground">Adres (slug){editor.builtin ? " — yerleşik sayfalarda sabittir" : ""}</p>
                                        <Input value={editor.slug} disabled={editor.builtin} onChange={(e) => setEditor({ ...editor, slug: slugify(e.target.value) })} data-testid="page-slug-input" />
                                    </div>
                                </div>
                                <label className="flex items-center gap-2 text-sm">
                                    <input type="checkbox" checked={editor.active} onChange={(e) => setEditor({ ...editor, active: e.target.checked })} className="h-4 w-4 accent-terra-600" data-testid="page-active-checkbox" />
                                    Yayında (sitede görünür)
                                </label>
                                <div className="space-y-3">
                                    {editor.sections.map((s, i) => (
                                        <div key={i} className="rounded-xl border p-3" data-testid={`page-section-${i}`}>
                                            <div className="flex items-center gap-2">
                                                <Input placeholder={`Bölüm ${i + 1} başlığı`} value={s.h} onChange={(e) => setSection(i, { h: e.target.value })} data-testid={`section-h-${i}`} />
                                                <Button variant="ghost" size="icon" onClick={() => setEditor({ ...editor, sections: editor.sections.filter((_, j) => j !== i) })} data-testid={`section-remove-${i}`}>
                                                    <Trash2 className="h-4 w-4 text-muted-foreground" />
                                                </Button>
                                            </div>
                                            <Textarea rows={4} className="mt-2" placeholder="Bölüm içeriği — her satır ayrı paragraf" value={s.text} onChange={(e) => setSection(i, { text: e.target.value })} data-testid={`section-p-${i}`} />
                                        </div>
                                    ))}
                                    <Button variant="outline" size="sm" onClick={() => setEditor({ ...editor, sections: [...editor.sections, { h: "", text: "" }] })} data-testid="section-add">
                                        <Plus className="mr-1.5 h-3.5 w-3.5" /> Bölüm Ekle
                                    </Button>
                                </div>
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setEditor(null)}>Vazgeç</Button>
                                <Button onClick={save} disabled={busy} className="bg-terra-600 hover:bg-terra-700 text-white" data-testid="page-save-button">
                                    {busy ? "Kaydediliyor..." : editor.isNew ? "Sayfayı Oluştur" : "Kaydet"}
                                </Button>
                            </DialogFooter>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
